import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState('');
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default data যদি API fail করে
  const defaultContactInfo = {
    pageHeading: 'Contact Us',
    pageSubtitle: 'We\'re here to help with any questions or comments',
    email: 'ebusiness.contact@gmail.com',
    phone: '+8809647001250',
    address: 'Aftabnagar, Dhaka, Bangladesh',
    googleMapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.8971203935644!2d90.42902931498165!3d23.753056884589!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b85c740f17d1%3A0x8c3f9ba2dbfba9a8!2sAftabnagar%2C%20Dhaka!5e0!3m2!1sen!2sbd!4v1234567890123!5m2!1sen!2sbd',
    googleMapSearchQuery: 'Aftabnagar,Dhaka,Bangladesh',
    companyName: 'E-Business',
    companyDescription: 'Your trusted online shopping platform. We are committed to providing high-quality products and the best customer service. Don\'t hesitate to contact us for any questions or assistance.'
  };

  // API থেকে contact info load করুন
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('https://dynamicgadgets-server.sparkx-ai.shop/api/contact-info');
        if (response.data.success) {
          setContactInfo(response.data.contactInfo);
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError('Failed to load contact info');
        // Error হলে default data use করুন
        setContactInfo(defaultContactInfo);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    const apiUrl = 'https://dynamicgadgets-server.sparkx-ai.shop/contact';
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('যোগাযোগের ফর্ম সাবমিট করতে ত্রুটি:', error);
      setFormStatus('error');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // যদি contactInfo না থাকে তাহলে default use করুন
  const info = contactInfo || defaultContactInfo;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans" style={{ fontFamily: 'Amazon Ember, Arial, sans-serif' }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{info.pageHeading}</h1>
          <p className="text-gray-600">{info.pageSubtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-orange-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
              </svg>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Email</h3>
                <a href={`mailto:${info.email}`} className="text-sm text-blue-600 hover:text-orange-600 hover:underline">
                  {info.email}
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-orange-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.774a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
              </svg>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Phone</h3>
                <a href={`tel:${info.phone}`} className="text-sm text-blue-600 hover:text-orange-600 hover:underline">
                  {info.phone}
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-orange-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
              </svg>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Address</h3>
                <p className="text-sm text-gray-700">{info.address}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Send us a message</h2>

          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-1">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                placeholder="example@email.com"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-bold text-gray-700 mb-1">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                placeholder="Enter subject"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-1">
                Your Message
              </label>
              <textarea
                id="message"
                name="message"
                rows="6"
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm resize-none"
                placeholder="Write your message here..."
              ></textarea>
            </div>

            <div>
              <button
                onClick={handleSubmit}
                disabled={formStatus === 'submitting'}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-2 px-4 rounded border border-yellow-600 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>

          {formStatus === 'success' && (
            <div className="mt-6 bg-green-50 border border-green-200 text-green-800 p-4 rounded text-sm">
              <strong>Success!</strong> Your message has been sent successfully. We'll get back to you soon.
            </div>
          )}
          {formStatus === 'error' && (
            <div className="mt-6 bg-red-50 border border-red-200 text-red-800 p-4 rounded text-sm">
              <strong>Error!</strong> Failed to send message. Please try again.
            </div>
          )}
        </div>


        {/* Google Map Section - Address card এর পরে add করুন */}
        <div className="mt-8 bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-300">
            <h3 className="text-lg font-bold text-gray-900">Find Us Here</h3>
            <p className="text-sm text-gray-600 mt-1">{info.address}</p>
          </div>
          <div className="relative w-full h-96">
            <iframe
              src={info.googleMapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Dynamic Gadgets Location"
              className="grayscale-0 hover:grayscale-0 transition-all duration-300"
            ></iframe>
          </div>
          <div className="p-4 bg-gray-50 border-t border-gray-300">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${info.googleMapSearchQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-blue-600 hover:text-orange-600 hover:underline font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
              </svg>
              View on Google Maps
            </a>
          </div>
        </div>

        <div className="mt-8 bg-gray-100 border border-gray-300 rounded-lg p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-3">{info.companyName}</h3>
          <p className="text-sm text-gray-700 leading-relaxed max-w-2xl mx-auto">
            {info.companyDescription}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;